/* Cross-domain orchestration. Individual stores stay domain-local; anything that
   touches more than one (parents ↔ room-parent access ↔ activity log) lives here
   so pages call one function and behavior matches the approved prototype. */
import useParentsStore from './useParentsStore';
import useRoomParentsStore from './useRoomParentsStore';
import useContextStore, { selectCurrentContext } from './useContextStore';
import { AVATAR_COLORS, initialsOf } from '../utils/avatar';
import { showToast } from './useToastStore';

const currentClassName = () => selectCurrentContext(useContextStore.getState()).name;

export function toggleRoomParent(id, checked) {
  const parentsStore = useParentsStore.getState();
  const rpStore = useRoomParentsStore.getState();
  const p = parentsStore.parents.find((x) => x.id === id);
  if (!p) return;
  parentsStore.setIsRoomParent(id, checked);
  if (checked) {
    if (!rpStore.hasAccess(p.name)) {
      rpStore.grantAccess({ name: p.name, initials: p.initials, color: p.color, classes: [currentClassName()] });
    }
  } else {
    rpStore.removeAccess(p.name);
  }
  showToast(checked ? `${p.name} now has Room Parent access ✓` : `${p.name} removed from Room Parent access`);
}

export function addParentWithRole({ name, kid, email, phone, isRoomParent }) {
  const parent = useParentsStore.getState().addParent({ name, kid, email, phone, isRoomParent });
  if (isRoomParent) {
    useRoomParentsStore.getState().grantAccess({
      name: parent.name,
      initials: parent.initials,
      color: parent.color,
      classes: [currentClassName()],
    });
  }
  showToast(
    isRoomParent
      ? `${name} added with Room Parent access ✓`
      : 'Parent added — they will also get event invitations by email ✓'
  );
}

export function grantRoomParentAccess({ parentId, customName, className, phone, email }) {
  const parentsStore = useParentsStore.getState();
  const rpStore = useRoomParentsStore.getState();
  const existing = parentId ? parentsStore.parents.find((p) => p.id === parentId) : null;
  const name = existing ? existing.name : customName.trim();
  if (!name) {
    window.alert('Select an existing parent or type a name.');
    return false;
  }
  if (rpStore.hasAccess(name)) {
    window.alert(`${name} already has access.`);
    return false;
  }
  let initials, color;
  if (existing) {
    initials = existing.initials;
    color = existing.color;
    parentsStore.setIsRoomParent(existing.id, true);
    if (phone) parentsStore.setParentPhone(existing.id, phone);
    if (email) parentsStore.setParentEmail(existing.id, email);
  } else {
    initials = initialsOf(name);
    color = AVATAR_COLORS[rpStore.access.length % AVATAR_COLORS.length];
  }
  rpStore.grantAccess({ name, initials, color, classes: [className] });
  showToast(`${name} granted Room Parent access to ${className} ✓`);
  return true;
}

export function revokeRoomParentAccess(name) {
  useRoomParentsStore.getState().removeAccess(name);
  useParentsStore.getState().setIsRoomParentByName(name, false);
  showToast(`Removed ${name}'s Room Parent access`);
}
